import * as React from 'react';
import { Button } from '../../components/primitives';
import { cn } from '../../lib/utils';

export type ImageFieldShape = 'square' | 'wide';

/**
 * Which of the studied treatments to draw. Under review — see
 * ImageFieldStudy.stories.tsx. Only one of these survives.
 */
export type ImageFieldTreatment = 'tile' | 'tile-caption' | 'row';

export interface ImageFieldProps {
  label: string;
  /** What the image has to be, said before it is chosen rather than after it fails. */
  requirement: string;
  shape?: ImageFieldShape;
  treatment?: ImageFieldTreatment;
  /** The current image, if there is one. */
  src?: string;
  disabled?: boolean;
  error?: string;
  warning?: string;
  onPick?: () => void;
  onRemove?: () => void;
  /** "Remove" normally; "Undo" once this edit has changed it. */
  removeLabel?: string;
  /**
   * Drop the field's own label. Set when the panel around it already carries
   * the same word — the banner sat in a panel headed "Banner" under a field
   * labelled "Banner". The label still names the picture for assistive tech.
   */
  hideLabel?: boolean;
  testId?: string;
}

const SHAPE: Record<ImageFieldShape, { frame: string; empty: string }> = {
  square: { frame: 'h-36 w-36', empty: 'h-36 w-36' },
  wide: { frame: 'h-[74px] w-full', empty: 'h-[74px] w-full' },
};

/**
 * The plate an image sits on: the picture if there is one, an empty frame if
 * there is not. Never a 192px grey icon — a placeholder that large reads as a
 * broken image rather than as an empty field.
 */
const Plate: React.FC<{
  src?: string;
  shape: ImageFieldShape;
  label: string;
  className?: string;
  children?: React.ReactNode;
}> = ({ src, shape, label, className, children }) => (
  <div
    className={cn(
      'relative overflow-hidden rounded-lg border border-border bg-muted',
      SHAPE[shape].frame,
      className,
    )}
  >
    {src ? (
      <img src={src} alt={`${label} preview`} className="h-full w-full object-cover" />
    ) : (
      <div className="flex h-full w-full items-center justify-center">
        <span className="text-xs text-muted-foreground">No {label.toLowerCase()} yet</span>
      </div>
    )}
    {children}
  </div>
);

/**
 * One image field, in three treatments under review.
 *
 * Today logo, profile picture and banner are three hand-written components of
 * about a hundred and sixty lines each, near-identical apart from a size and a
 * noun — three copies of the same preview/upload/revert/remove state machine,
 * two spellings of the label ("Logo", "Profile Image"), hardcoded greys instead
 * of theme tokens, and a grey placeholder icon nearly two hundred pixels across.
 * Whichever treatment is chosen, they become one component.
 */
export const ImageField: React.FC<ImageFieldProps> = ({
  label,
  requirement,
  shape = 'square',
  treatment = 'tile',
  src,
  disabled,
  error,
  warning,
  onPick,
  onRemove,
  removeLabel = 'Remove',
  hideLabel = false,
  testId,
}) => {
  // `onImage` matters: a ghost button is muted foreground, which on a scrim
  // over a dark photograph is dark on dark. The treatment that puts controls on
  // the picture has to light them for it.
  const buildActions = (onImage = false) => (
    <>
      <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={onPick}>
        {src ? 'Change' : 'Upload'}
      </Button>
      {src && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled}
          onClick={onRemove}
          className={onImage ? 'text-white hover:bg-white/15 hover:text-white' : undefined}
        >
          {removeLabel}
        </Button>
      )}
    </>
  );
  const actions = buildActions();

  const messages = (
    <>
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
      {warning && <p className="mt-2 text-xs text-amber-700">{warning}</p>}
    </>
  );

  // A — the design's own treatment for the banner, applied to all three: the
  // picture is the control, and the actions sit on it.
  if (treatment === 'tile') {
    return (
      <div data-testid={testId}>
        <div className="flex items-baseline justify-between gap-3">
          <span className={hideLabel ? 'sr-only' : 'text-sm font-medium text-foreground'}>{label}</span>
          <span className={cn('text-xs text-muted-foreground', hideLabel && 'ml-auto')}>
            {requirement}
          </span>
        </div>
        <Plate src={src} shape={shape} label={label} className="mt-1.5">
          {/* A bar across the bottom edge, not two buttons floating in the
              corner: a 112px square is narrower than "Change" and "Remove"
              side by side, so the corner treatment the design draws for a
              1200px banner clipped its own controls on the logo. */}
          <span
            className={cn(
              'absolute inset-x-0 bottom-0 flex items-center justify-end gap-1.5 px-1.5 py-1.5',
              // The scrim exists to lift the controls off a photograph. On an
              // empty frame there is no photograph, and it just reads as a
              // grey smudge across the bottom of the box.
              src && 'bg-gradient-to-t from-foreground/55 to-transparent',
            )}
          >
            {buildActions(!!src)}
          </span>
        </Plate>
        {messages}
      </div>
    );
  }

  // B — same plate, actions and requirement underneath. The actions never sit
  // on top of the picture, which matters most when the picture is dark.
  if (treatment === 'tile-caption') {
    return (
      <div data-testid={testId}>
        <span className={hideLabel ? 'sr-only' : 'text-sm font-medium text-foreground'}>{label}</span>
        <Plate src={src} shape={shape} label={label} className="mt-1.5" />
        <div className="mt-2 flex items-center gap-1.5">
          {actions}
          <span className="ml-auto text-xs text-muted-foreground">{requirement}</span>
        </div>
        {messages}
      </div>
    );
  }

  // C — what is there today, tidied: thumbnail beside the controls.
  return (
    <div data-testid={testId}>
      <span className={hideLabel ? 'sr-only' : 'text-sm font-medium text-foreground'}>{label}</span>
      <div className="mt-1.5 flex items-center gap-4">
        <Plate src={src} shape={shape} label={label} className={shape === 'wide' ? 'max-w-[240px]' : undefined} />
        <div>
          <div className="flex items-center gap-1.5">{actions}</div>
          <p className="mt-2 text-xs text-muted-foreground">{requirement}</p>
        </div>
      </div>
      {messages}
    </div>
  );
};
